import { FiBriefcase, FiClipboard, FiGlobe, FiSend } from "react-icons/fi";
import type { IconType } from "react-icons";
import type { ScopeType } from "../types/RolesPermissions.type";

interface ScopeTypeStyle {
	/** Human label for the scope type, as the product names it. */
	label: string;
	/** Tailwind classes for a pill/chip carrying a scope of this type. */
	chip: string;
	/**
	 * Author item 4.4 — a SELECTED row in the scope picker's search results is tinted in its
	 * section's colour family (left border + background), not the flat blue every type
	 * previously shared. Composes with a plain `border-l-transparent` for the unselected state.
	 */
	rowSelectedBg: string;
	/** Selected-row label text colour, same family as `rowSelectedBg`/`chip`. */
	rowSelectedText: string;
	/**
	 * Author item F7 — one icon per scope type, matching `ManageRolesTable.tsx`'s own
	 * (module-private) `SCOPE_TYPE_ICON` convention (`FiGlobe`/`FiBriefcase`/`FiClipboard`/
	 * `FiFile`) — the same icons the deleted `Step2ScopeSearch.tsx` used before D10's rewrite
	 * dropped them (ticket line 44: icons matching the access level, on every row). Placed here,
	 * alongside the colour each type already shares, rather than declared a third time — this is
	 * the ONE per-type style object every RBAC surface reads from.
	 */
	icon: IconType;
	/**
	 * Author retest 1.1/2.1 — the author asked for RFx's icon specifically:
	 * feather's "send" (paper plane, matching items.png), rotated so the tip
	 * points horizontally right rather than its default ~45° upper-right. A
	 * rotation is visual styling, not a different icon choice, so it lives
	 * here as an EXTRA class merged alongside each consumer's own size/colour
	 * classes — one place to tweak the angle (or drop it) later, rather than
	 * a `rotate-*` class hardcoded at both render call sites
	 * (`ScopePickerList.tsx`'s row icon, `AccessModeSelector.tsx`'s
	 * Company-Wide/Specific-Access cards) independently. Empty string for
	 * every type that needs no rotation.
	 */
	iconClassName: string;
}

/**
 * One colour per scope type, shared by every surface that shows a specific
 * allocation.
 *
 * Product asked for these three to be distinguishable at a glance — Portfolio
 * green, Project yellow, RFx blue — and they were previously coloured ad hoc:
 * the assign wizard used teal for RFx and the SAME blue for both portfolios and
 * projects, which made the two hardest-to-tell-apart types identical. Colour is
 * doing real work here, so it lives in one place rather than at each call site.
 *
 * `global` stays deliberately neutral: it is not one of the three specific
 * levels, and giving it a colour of its own would imply it belongs to the same
 * scale.
 */
export const SCOPE_TYPE_STYLE: Record<ScopeType, ScopeTypeStyle> = {
	global: {
		label: "Company Wide",
		chip: "border-gray-300 bg-gray-50 text-gray-700",
		rowSelectedBg: "border-l-gray-400 bg-gray-50",
		rowSelectedText: "text-gray-900",
		icon: FiGlobe,
		iconClassName: "",
	},
	project_type: {
		label: "Portfolio",
		chip: "border-emerald-300 bg-emerald-50 text-emerald-800",
		rowSelectedBg: "border-l-emerald-500 bg-emerald-50",
		rowSelectedText: "text-emerald-900",
		icon: FiBriefcase,
		iconClassName: "",
	},
	project: {
		label: "Project",
		chip: "border-amber-300 bg-amber-50 text-amber-900",
		rowSelectedBg: "border-l-amber-500 bg-amber-50",
		rowSelectedText: "text-amber-900",
		// Author retest 1.1/2.1 — the author's correction named RFx only. The menu's own
		// project-type icon is ALSO a bespoke SVG asset (icons/projects-icon.svg, not a Feather
		// icon), so this is arguably the same mismatch — but changing it now, unasked, risks a
		// second unreviewed icon swap on a detail the author is actively iterating on. Left as
		// FiClipboard; flagged in the report for the author/coordinator to confirm or correct.
		icon: FiClipboard,
		iconClassName: "",
	},
	rfx: {
		label: "RFx",
		chip: "border-blue-300 bg-blue-50 text-blue-800",
		rowSelectedBg: "border-l-blue-500 bg-blue-50",
		rowSelectedText: "text-blue-900",
		// Author retest 1.1/2.1 — feather "send" (paper plane, matches items.png), rotated 45°
		// clockwise from its default ~45°-upper-right heading so the tip points horizontally
		// right. See `iconClassName`'s doc comment on why the rotation lives here, not at each
		// render call site.
		icon: FiSend,
		iconClassName: "rotate-45",
	},
};

export const scopeTypeStyle = (type: string): ScopeTypeStyle =>
	SCOPE_TYPE_STYLE[type as ScopeType] ?? {
		label: type,
		chip: "border-gray-300 bg-gray-50 text-gray-700",
		rowSelectedBg: "border-l-gray-400 bg-gray-50",
		rowSelectedText: "text-gray-900",
		icon: FiGlobe,
		iconClassName: "",
	};
